import type {
  PlaybookId,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type {
  FeedbackWithCurrentGrade,
  RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { useOutletContext } from "react-router";
import type {
  PlaybookEditorData,
  UsePlaybookEditorResult,
} from "../hooks/usePlaybookEditor.js";
import type { PlaybookEditorContainerProps } from "./playbookEditorContainer.js";
import type { PlaybookEditorSelectionId } from "./playbookEditorUrlState.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export type EditorContainerRouteProps = Omit<
  PlaybookEditorContainerProps,
  "initialSelectedKey" | "onCurrentSelectionChange"
>;

export interface PlaybookEditorOutletContext {
  playbookIdParam: string;
  playbookId: PlaybookId | null;
  data: PlaybookEditorData | undefined;
  liveTree: PlaybookTree | undefined;
  openFeedback: readonly FeedbackWithCurrentGrade[];
  isOpenFeedbackLoading: boolean;
  isOpenFeedbackError: boolean;
  showsKnowledgeBase: boolean;
  onChange: UsePlaybookEditorResult["onChange"];
  editorProps: EditorContainerRouteProps;
  /**
   * The selection the Edit and Feedback tab links carry, so switching tabs
   * lands on the same node. Reported by both tabs.
   */
  onTabSelectionChange: (selectionId: PlaybookEditorSelectionId | null) => void;
  /**
   * The tree item the edit tab has open, ghost rows included, so the chat can
   * mark the proposal on screen. Reported by the edit tab only; `undefined`
   * once it closes.
   */
  onViewedTreeItemChange: (key: AssessmentTreeItemKey | undefined) => void;
  reviewStatus: RecordingAssessmentFeedbackStatus;
  onReviewStatusChange: (status: RecordingAssessmentFeedbackStatus) => void;
}

export function usePlaybookEditorOutletContext(): PlaybookEditorOutletContext {
  return useOutletContext<PlaybookEditorOutletContext>();
}
